import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import { App } from './lib'
import i18n from './i18n'
import * as serviceWorker from './serviceWorker'

// The theme's own localization form (in the header, outside our control) has
// no return_to field on app-proxy pages like this one (e.g. /apps/ub/diamonds-ai,
// /apps/ub/pairs-ai) -- confirmed live: submitting it without one drops both the
// locale prefix and the -ai suffix, landing back on plain /apps/ub/diamonds.
// Patch any localization form submission to explicitly preserve the current
// path so switching language/region doesn't navigate away from this route.
document.addEventListener('submit', (event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement) || !/\/localization(\?|$)/.test(form.action)) {
    return
  }
  let returnTo = form.querySelector<HTMLInputElement>('input[name="return_to"]')
  if (!returnTo) {
    returnTo = document.createElement('input')
    returnTo.type = 'hidden'
    returnTo.name = 'return_to'
    form.appendChild(returnTo)
  }
  returnTo.value = window.location.pathname + window.location.search + window.location.hash
}, true)

// Shopify's Markets locale-prefixed URLs (e.g. /fr/apps/ub/diamonds-ai) put a
// language segment ahead of the app proxy path. Our routes are all defined as
// /apps/ub/... with no awareness of that prefix, so without accounting for it
// React Router matches nothing and falls through to App.tsx's catch-all
// <Redirect to={diamondsSearchPage} /> -- confirmed live: this, not any
// server-side redirect (a same-origin fetch of the locale-prefixed URL returns
// 200 with no Location header), is what was dropping the -ai suffix on
// language switch. Passing the detected locale segment as BrowserRouter's
// basename makes route matching ignore it, and makes history.push/<Link>
// keep prepending it automatically for the rest of the session.
const localeBasenameMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/apps\/ub\/)/i)
const routerBasename = localeBasenameMatch ? `/${localeBasenameMatch[1]}` : undefined

if (document.querySelector('#loading-unbridaled-app') !== null) {
  setTimeout(() => {
    document.querySelector('#loading-unbridaled-app')?.classList.add('loaded')
    document.querySelector('#loading-unbridaled-app')?.classList.add('unbridaled-loaded')
    setTimeout(() => {
      document.querySelector('#loading-unbridaled-app')?.remove()
    }, 100)
  }, 100)
}

const app = (
  // @unbridaled/search-sdk@4.2.2 bundles its own i18next instance and calls
  // .use(initReactI18next).init(...) on it, which overwrites react-i18next's
  // shared global default -- any bare useTranslation() call in this app would
  // otherwise silently rebind to the SDK's own narrow (en-US only) dictionary.
  // Binding our instance explicitly via I18nextProvider makes every
  // useTranslation() call here immune to that, regardless of module load order.
  <I18nextProvider i18n={i18n}>
    <BrowserRouter basename={routerBasename}>
      <App />
    </BrowserRouter>
  </I18nextProvider>
)
const rootElement = document.getElementById('iladiamondsearch')
if (rootElement) {
  const root = createRoot(rootElement)
  root.render(app)
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()